// ============================================================================
// OBT F3 — Filtre-dışı park önerileri (BM karar akışı)
// ============================================================================
// `GET /publications/{id}/suggested-parks` (F3 enabler) ile aktörün asli
// Formen'lerinin önerilerini yükler; onay/ret gerçek backend uçlarına bağlanır.
// Karar sonrası liste + üst sayaçlar tazelenir. Aynı öneri iki kez karara
// bağlanamaz (backend 409/invalid-state → mesaj + reload).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listSuggestedParks,
  approveSuggestedPark,
  rejectSuggestedPark,
} from '../reviewApi';
import { normalizeReviewError } from '../reviewErrors';

function isCanceled(error) {
  return (
    error?.code === 'ERR_CANCELED' ||
    error?.name === 'CanceledError' ||
    error?.name === 'AbortError'
  );
}

/**
 * @param {string|null} publicationId
 * @param {{status?: string, enabled?: boolean, onChanged?: () => void}} options
 */
export function useSuggestedParkReviews(
  publicationId,
  { status = 'pending_bm_review', enabled = true, onChanged } = {}
) {
  const [state, setState] = useState({
    loading: Boolean(publicationId) && enabled,
    error: null,
    suggestions: [],
  });
  const [actionError, setActionError] = useState(null);
  const [pendingId, setPendingId] = useState(null);
  const abortRef = useRef(null);
  const onChangedRef = useRef(onChanged);
  onChangedRef.current = onChanged;

  const load = useCallback(async () => {
    if (!publicationId || !enabled) {
      setState({ loading: false, error: null, suggestions: [] });
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const suggestions = await listSuggestedParks(
        publicationId,
        { status },
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      setState({ loading: false, error: null, suggestions });
    } catch (error) {
      if (isCanceled(error) || controller.signal.aborted) return;
      setState({ loading: false, error: normalizeReviewError(error), suggestions: [] });
    }
  }, [publicationId, status, enabled]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const runDecision = useCallback(
    async (suggestionId, fn) => {
      if (pendingId) return false;
      setPendingId(suggestionId);
      setActionError(null);
      try {
        await fn();
        await load();
        onChangedRef.current?.();
        setPendingId(null);
        return true;
      } catch (error) {
        if (isCanceled(error)) {
          setPendingId(null);
          return false;
        }
        const normalized = normalizeReviewError(error);
        setActionError(normalized);
        if (normalized.status === 409 || normalized.status === 422) {
          await load();
        }
        setPendingId(null);
        throw error;
      }
    },
    [pendingId, load]
  );

  const approve = useCallback(
    (suggestionId, comment) =>
      runDecision(suggestionId, () =>
        approveSuggestedPark(suggestionId, { comment: comment || null })
      ),
    [runDecision]
  );
  const reject = useCallback(
    (suggestionId, comment) =>
      runDecision(suggestionId, () => rejectSuggestedPark(suggestionId, { comment })),
    [runDecision]
  );

  return {
    ...state,
    reload: load,
    approve,
    reject,
    pendingId,
    actionError,
    clearActionError: () => setActionError(null),
  };
}
